import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BookOpen, Brain, CheckCircle2, Clock, KeyRound, RotateCcw, Shield, Volume2, XCircle } from 'lucide-react';
import './styles.css';

type Role = 'student' | 'admin';
type Module = 'sentence' | 'vocabulary';
type Status = 'correct' | 'ambiguous' | 'incorrect';

interface SentenceItem {
  id: number;
  sentence: string;
  topic: string;
  difficulty: number;
  word_count: number;
  time_limit_seconds: number;
  grammar_focus: string;
}

interface WordItem {
  id: number;
  word: string;
  phonetic: string;
  part_of_speech: string;
  meanings_zh: string;
  example_en: string;
  difficulty: number;
  topic: string;
}

interface Feedback {
  status: Status;
  score: number;
  summary: string;
  vocabulary_issues?: Array<{ word: string; correct_meaning: string; reason: string }>;
  grammar_issues?: Array<{ structure: string; issue: string; example: string }>;
  translation_feedback?: string;
  suggested_translation?: string;
  correct_meaning?: string;
  missed_meanings?: string[];
  accepted_meanings?: string[];
}

const tokenKey = 'zhc-token';

function useApi() {
  const [token, setToken] = useState(localStorage.getItem(tokenKey) || '');
  const saveToken = (next: string) => {
    localStorage.setItem(tokenKey, next);
    setToken(next);
  };
  const logout = () => {
    localStorage.removeItem(tokenKey);
    setToken('');
  };
  async function api<T>(path: string, init: RequestInit = {}) {
    const response = await fetch(`/api${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init.headers
      }
    });
    const data = await response.json() as { error?: string; token: string; role: Role };
    if (!response.ok) throw new Error(data.error || '请求失败');
    return data as T;
  }
  return { token, saveToken, logout, api };
}

function statusLabel(status?: Status) {
  if (status === 'correct') return '正确';
  if (status === 'ambiguous') return '有歧义';
  if (status === 'incorrect') return '错误';
  return '待判分';
}

function formatTime(seconds: number) {
  const min = Math.floor(Math.max(0, seconds) / 60);
  const sec = Math.max(0, seconds) % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

function Login({ onLogin }: { onLogin: (token: string, role: Role) => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await response.json() as { error?: string; token: string; role: Role };
      if (!response.ok) throw new Error(data.error || '登录失败');
      onLogin(data.token, data.role);
    } catch (err) {
      setError(err instanceof Error ? err.message : '登录失败');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-panel">
        <div className="brand-row">
          <Brain size={30} />
          <div>
            <h1>Seth's English</h1>
            <p>托福长难句与词汇训练</p>
          </div>
        </div>
        <form onSubmit={submit} className="login-form">
          <label htmlFor="password">访问密码</label>
          <div className="password-row">
            <KeyRound size={18} />
            <input id="password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus />
          </div>
          {error && <p className="error-text">{error}</p>}
          <button disabled={loading || !password}>{loading ? '登录中...' : '登录'}</button>
        </form>
      </section>
    </main>
  );
}

function StatsStrip({ stats }: { stats: any }) {
  const todayRows = stats?.daily || [];
  const sentence = todayRows.find((row: any) => row.module === 'sentence');
  const vocab = todayRows.find((row: any) => row.module === 'vocabulary');
  return (
    <div className="stats-strip">
      <div><span>今日长难句</span><strong>{sentence?.attempts || 0}</strong></div>
      <div><span>今日单词</span><strong>{vocab?.attempts || 0}</strong></div>
      <div><span>今日正确</span><strong>{(sentence?.correct || 0) + (vocab?.correct || 0)}</strong></div>
      <div><span>日期</span><strong>{stats?.today || '-'}</strong></div>
    </div>
  );
}

function SentencePractice({ api, onStats }: { api: ReturnType<typeof useApi>['api']; onStats: (stats: any) => void }) {
  const [item, setItem] = useState<SentenceItem | null>(null);
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const startedAt = useRef(Date.now());

  async function loadNext() {
    const data = await api<{ item: SentenceItem }>('/sentence/next');
    setItem(data.item);
    setAnswer('');
    setFeedback(null);
    setError('');
    setRemaining(data.item?.time_limit_seconds || 0);
    startedAt.current = Date.now();
  }

  useEffect(() => {
    void loadNext();
  }, []);

  useEffect(() => {
    if (!item || feedback) return;
    const timer = window.setInterval(() => setRemaining((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [item, feedback]);

  async function submit() {
    if (!item || !answer.trim()) return;
    setLoading(true);
    setError('');
    const elapsedSeconds = Math.round((Date.now() - startedAt.current) / 1000);
    try {
      const data = await api<{ feedback: Feedback; stats: any }>('/sentence/submit', {
        method: 'POST',
        body: JSON.stringify({ itemId: item.id, answer, elapsedSeconds })
      });
      setFeedback(data.feedback);
      onStats(data.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : '提交失败');
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="practice-surface">
      <div className="surface-header">
        <div>
          <h2>长难句分析</h2>
          <p>{item ? `${item.topic} · ${item.word_count} 词 · 难度 ${item.difficulty}` : '加载中'}</p>
        </div>
        <div className={`timer ${remaining === 0 ? 'danger' : ''}`}><Clock size={18} />{formatTime(remaining)}</div>
      </div>
      {item && (
        <>
          <p className="sentence-text">{item.sentence}</p>
          <div className="meta-line">重点结构：{item.grammar_focus}</div>
          <textarea value={answer} onChange={(event) => setAnswer(event.target.value)} placeholder="输入你的中文理解或翻译" />
          <div className="action-row">
            <button onClick={submit} disabled={loading || !answer.trim()}>{loading ? 'AI 判分中...' : '提交判分'}</button>
            <button className="secondary" onClick={loadNext}>下一句</button>
          </div>
        </>
      )}
      {feedback && <FeedbackPanel feedback={feedback} />}
      {error && <p className="error-text">{error}</p>}
    </section>
  );
}

function WordPractice({ api, onStats }: { api: ReturnType<typeof useApi>['api']; onStats: (stats: any) => void }) {
  const [item, setItem] = useState<WordItem | null>(null);
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const startedAt = useRef(Date.now());

  async function loadNext() {
    const data = await api<{ item: WordItem }>('/word/next');
    setItem(data.item);
    setAnswer('');
    setFeedback(null);
    setError('');
    startedAt.current = Date.now();
  }

  useEffect(() => {
    void loadNext();
  }, []);

  function speak() {
    if (!item) return;
    const audio = new Audio(`https://dict.youdao.com/dictvoice?type=2&audio=${encodeURIComponent(item.word)}`);
    audio.play().catch(() => {
      const utterance = new SpeechSynthesisUtterance(item.word);
      utterance.lang = 'en-US';
      utterance.rate = 0.82;
      speechSynthesis.cancel();
      speechSynthesis.speak(utterance);
    });
  }

  async function submit(forcedAnswer?: string) {
    const finalAnswer = forcedAnswer ?? answer;
    if (!item || !finalAnswer.trim()) return;
    setLoading(true);
    setError('');
    const elapsedSeconds = Math.round((Date.now() - startedAt.current) / 1000);
    try {
      const data = await api<{ feedback: Feedback; stats: any }>('/word/submit', {
        method: 'POST',
        body: JSON.stringify({ itemId: item.id, answer: finalAnswer, elapsedSeconds })
      });
      setFeedback(data.feedback);
      setAnswer(finalAnswer);
      onStats(data.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : '提交失败');
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="practice-surface">
      <div className="surface-header">
        <div>
          <h2>单词背诵</h2>
          <p>{item ? `${item.part_of_speech} · ${item.topic} · 难度 ${item.difficulty}` : '加载中'}</p>
        </div>
        <button className="icon-button" onClick={speak} aria-label="播放发音"><Volume2 size={20} /></button>
      </div>
      {item && (
        <>
          <div className="word-card">
            <strong>{item.word}</strong>
            <span>{item.phonetic}</span>
          </div>
          <input className="answer-input" value={answer} onChange={(event) => setAnswer(event.target.value)} placeholder="输入中文释义" onKeyDown={(event) => event.key === 'Enter' && void submit()} />
          <div className="action-row">
            <button onClick={() => void submit()} disabled={loading || !answer.trim()}>{loading ? '判分中...' : '提交'}</button>
            <button className="secondary" onClick={() => void submit('我不知道')} disabled={loading}>我不知道</button>
            <button className="secondary" onClick={loadNext}>下一个</button>
          </div>
        </>
      )}
      {feedback && <FeedbackPanel feedback={feedback} />}
      {error && <p className="error-text">{error}</p>}
    </section>
  );
}

function FeedbackPanel({ feedback }: { feedback: Feedback }) {
  const Icon = feedback.status === 'correct' ? CheckCircle2 : XCircle;
  return (
    <div className={`feedback ${feedback.status}`}>
      <div className="feedback-title">
        <Icon size={20} />
        <strong>{statusLabel(feedback.status)} · {feedback.score} 分</strong>
      </div>
      <p>{feedback.summary}</p>
      {feedback.correct_meaning && <div className="answer-key"><strong>正确释义：</strong>{feedback.correct_meaning}</div>}
      {feedback.translation_feedback && <p>{feedback.translation_feedback}</p>}
      {feedback.suggested_translation && <div className="answer-key">{feedback.suggested_translation}</div>}
      {!!feedback.vocabulary_issues?.length && (
        <div className="issue-list">
          <strong>词汇问题</strong>
          {feedback.vocabulary_issues.map((issue) => <p key={issue.word}>{issue.word}：{issue.correct_meaning}。{issue.reason}</p>)}
        </div>
      )}
      {!!feedback.grammar_issues?.length && (
        <div className="issue-list">
          <strong>语法结构</strong>
          {feedback.grammar_issues.map((issue) => <p key={issue.structure}>{issue.structure}：{issue.issue} 示例：{issue.example}</p>)}
        </div>
      )}
    </div>
  );
}

function Notebook({ api }: { api: ReturnType<typeof useApi>['api'] }) {
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => {
    api<{ items: any[] }>('/notebook').then((data) => setItems(data.items));
  }, []);
  return (
    <section className="practice-surface">
      <h2>生词本</h2>
      <div className="notebook-list">
        {items.length === 0 && <p className="muted">暂无生词。</p>}
        {items.map((item) => (
          <article key={item.id} className="notebook-row">
            <strong>{item.word}</strong>
            <span>{item.correct_meaning}</span>
            <small>{item.source_category}</small>
          </article>
        ))}
      </div>
    </section>
  );
}

function Admin({ api }: { api: ReturnType<typeof useApi>['api'] }) {
  const [summary, setSummary] = useState<any>(null);
  const [message, setMessage] = useState('');
  async function load() {
    const data = await api('/admin/summary');
    setSummary(data);
  }
  useEffect(() => {
    void load();
  }, []);
  async function reset() {
    await api('/admin/reset?userId=student-main', { method: 'POST' });
    setMessage('普通用户进度已重置。');
    await load();
  }
  async function resetAll() {
    await api('/admin/reset-all', { method: 'POST' });
    setMessage('全部历史记录、进度和生词本已重置。');
    await load();
  }
  return (
    <section className="practice-surface">
      <div className="surface-header">
        <div>
          <h2>管理后台</h2>
          <p>题库、用户和进度控制</p>
        </div>
        <Shield size={24} />
      </div>
      <div className="admin-grid">
        {summary?.counts?.map((row: any) => <div key={row.table_name}><span>{row.table_name}</span><strong>{row.count}</strong></div>)}
      </div>
      <div className="action-row">
        <button className="danger-button" onClick={reset}><RotateCcw size={17} />重置普通用户进度</button>
        <button className="danger-button" onClick={resetAll}><RotateCcw size={17} />重置全部</button>
      </div>
      {message && <p className="success-text">{message}</p>}
    </section>
  );
}

function App() {
  const apiState = useApi();
  const [role, setRole] = useState<Role | null>(null);
  const [active, setActive] = useState<Module | 'notebook' | 'admin'>('sentence');
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    if (!apiState.token) return;
    apiState.api<{ role: Role; stats: any }>('/me').then((data) => {
      setRole(data.role);
      setStats(data.stats);
      if (data.role === 'admin') setActive('admin');
    }).catch(apiState.logout);
  }, [apiState.token]);

  const tabs = useMemo(() => [
    { id: 'sentence', label: '长难句', icon: BookOpen },
    { id: 'vocabulary', label: '单词', icon: Brain },
    { id: 'notebook', label: '生词本', icon: CheckCircle2 },
    ...(role === 'admin' ? [{ id: 'admin', label: '管理', icon: Shield }] : [])
  ], [role]);

  if (!apiState.token || !role) {
    return <Login onLogin={(nextToken, nextRole) => {
      apiState.saveToken(nextToken);
      setRole(nextRole);
    }} />;
  }

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <div className="brand-row compact"><Brain size={26} /><h1>Seth's English</h1></div>
        <nav>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return <button key={tab.id} className={active === tab.id ? 'active' : ''} onClick={() => setActive(tab.id as any)}><Icon size={18} />{tab.label}</button>;
          })}
        </nav>
        <button className="logout" onClick={apiState.logout}>退出</button>
      </aside>
      <section className="main-column">
        <StatsStrip stats={stats} />
        {active === 'sentence' && <SentencePractice api={apiState.api} onStats={setStats} />}
        {active === 'vocabulary' && <WordPractice api={apiState.api} onStats={setStats} />}
        {active === 'notebook' && <Notebook api={apiState.api} />}
        {active === 'admin' && <Admin api={apiState.api} />}
      </section>
    </main>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
